import React, { ReactNode, ElementType, ComponentPropsWithoutRef } from 'react';
import { classNames } from '@repo/ui/src/utils/classNames';

type MaxWidth = 'narrow' | 'default' | 'wide' | 'extra-wide' | 'full';

const maxWidthClasses: Record<MaxWidth, string> = {
  narrow: 'max-w-3xl',
  default: 'max-w-7xl',
  wide: 'max-w-[96rem]',
  'extra-wide': 'max-w-[120rem]',
  full: 'max-w-none',
};

type Props<T extends ElementType> = {
  as?: T;
  children: ReactNode;
  className?: string;
  dataTestId?: string;
  maxWidth?: MaxWidth;
} & Omit<ComponentPropsWithoutRef<T>, 'as' | 'children' | 'className' | 'dataTestId' | 'maxWidth'>;

const Container = <T extends ElementType = 'div'>({
  as,
  children,
  className = '',
  dataTestId,
  maxWidth = 'full',
  ...rest
}: Props<T>) => {
  const Component = as || 'div';

  return (
    <Component
      className={classNames(
        'px-4 sm:px-10 lg:px-20',
        maxWidthClasses[maxWidth],
        maxWidth !== 'full' && 'mx-auto',
        className,
      )}
      data-testid={dataTestId}
      {
        ...(rest as any) /* Cast needed: TS cannot narrow ComponentPropsWithoutRef<T> through generic spread */
      }
    >
      {children}
    </Component>
  );
};

export default Container;
